import CompanyLogo from '../company/CompanyLogo';
import UserBadge from '../profile/UserBadge';
import CompanyDetailsModal from '../../modals/CompanyDetailsModal';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import useInitialData from '../../hooks/global/useInitialData';
import { Link } from 'react-router-dom';
import useRouter from '../../hooks/navigation/useRouter';
import useMenu from '../../hooks/menu/useMenu';
import { collect } from 'collect.js';
import classNames from 'classnames';
import useTheme from '../../hooks/ui/useTheme';
import BackIcon from '../../assets/logo/BackIcon';
import { useTranslation } from 'react-i18next';
import { SCREENS } from '../../services/exports/Constants';
import useRouteMatch from '../../hooks/navigation/useRouteMatch';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useStorefront from '../../hooks/storefront/useStorefront';
import { StorefrontPages } from '../../modules/storefront/exports/Enums';
import OrderBasketButton from '../order-basket/OrderBasketButton';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import useAuth from '../../hooks/global/useAuth';
import useScreenType from '../../hooks/utility/useScreenType';
import { ArrowUpRight, Star } from 'lucide-react';
import useStorefrontRouter from '../../modules/storefront/hooks/useStorefrontRouter';
import LanguageSelector from '../languages/LanguageSelector';
import TestRestaurantBadge from '../company/TestRestaurantBadge';

type NavItemType = 'link' | 'button';

interface INavItem {
  label: ReactNode;
  to?: string;
  type: NavItemType;
  isActive?: boolean;
  disableActive?: boolean;
  onClick?: () => void;
  onClicked?: () => void;
  className?: string;
  textClassName?: string;
  iconRight?: ReactNode;
  style?: React.CSSProperties;
  testId?: string;
}

interface IBack {
  onClick: () => void;
  title: string;
}

const PageHeader = (): ReactElement => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Headers:PageHeader' });
  const { data } = useInitialData();
  const { company, franchise } = data;
  const { generatePath } = useRouter();
  const { allCategories, generateMenuScrollQuery } = useMenu();
  const routeMatch = useRouteMatch();
  const { isStorefront } = useStorefront();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);
  const { toggleOrderMethodModal } = useAppMethods();
  const openingStatus = useOpeningStatus();
  const { hasCashbackProgram, balance } = useCashbackProgram();
  const { isLoggedIn } = useAuth();
  const { isMobile } = useScreenType();
  const { navigate: navigateStorefront } = useStorefrontRouter();

  const items = useMemo(() => {
    const navItems: INavItem[] = [];

    navItems.push({
      label: t('nav_items.menu'),
      to: `${generatePath(SCREENS.HOME_SCREEN)}${generateMenuScrollQuery(
        collect(allCategories).first()?.id,
        routeMatch(SCREENS.HOME_SCREEN) ? 'smooth' : 'auto',
      )}`,
      type: 'link',
    });

    if (hasCashbackProgram) {
      const rewardsLabel: ReactNode =
        isMobile && isLoggedIn ? (
          <span className="inline-flex items-center gap-1">
            {t('nav_items.rewards')}
            <span className="text-brand-primary font-semibold inline-flex items-center gap-1">
              ·
              <Star className="w-3 h-3 fill-current" />
              {balance}
              {' pts'}
            </span>
          </span>
        ) : (
          t('nav_items.rewards')
        );

      navItems.push({
        label: rewardsLabel,
        to: generatePath(SCREENS.REWARDS_SCREEN),
        type: 'link',
      });
    }

    if (franchise?.has_multiple_companies) {
      navItems.push({
        label: t('nav_items.locations'),
        type: 'button',
        onClick: () => toggleOrderMethodModal({ openModal: true }),
      });
    }

    navItems.push({
      label: t('nav_items.store_info'),
      type: 'button',
      testId: 'store-info-button',
      onClick: () => setShowCompanyDetailsModal(true),
    });

    if (isStorefront) {
      navItems.push({
        label: t('nav_items.home'),
        type: 'button',
        onClick: () => navigateStorefront(StorefrontPages.Home),
        iconRight: <ArrowUpRight className="w-4 h-4" />,
        disableActive: true,
      });
    }

    return navItems;
  }, [
    isStorefront,
    franchise?.has_multiple_companies,
    hasCashbackProgram,
    allCategories,
    routeMatch,
    isMobile,
    isLoggedIn,
    balance,
    navigateStorefront,
  ]);

  const renderCompanyDetailsModal = useMemo<ReactElement>(() => {
    return (
      <CompanyDetailsModal
        data={company}
        openModal={showCompanyDetailsModal}
        toggleModal={() => setShowCompanyDetailsModal(false)}
      />
    );
  }, [company, showCompanyDetailsModal]);

  return (
    <>
      <div className={headerWrapper}>
        {/* Row 1 — Identity bar */}
        <div id="header-identity-row" className={identityRow}>
          <div className={logoGroup}>
            {!isMobile && <CompanyLogo />}
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-xs">
                <h1 className={companyName}>{company?.name}</h1>
                <TestRestaurantBadge company={company} />
              </div>
              <p className={companyAddress}>
                {company?.address}
                {company?.city ? `, ${company.city}` : ''}
                {company?.address && ' · '}
                <span className={openingStatus.is_open ? 'text-green-600' : 'text-red-500'}>
                  {openingStatus.details_text}
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-x-sm sm:gap-x-md">
            <LanguageSelector />
            <UserBadge />
            <OrderBasketButton />
          </div>
        </div>

        {/* Row 2 — Tab navigation */}
        <div className={tabRow}>
          <nav className={tabNav}>
            {items.map((item, index) => {
              const isActive = !item.disableActive && item.to ? routeMatch(item.to.split('?')[0]) : false;

              return (
                <PageHeader.NavigationLink
                  key={index}
                  {...item}
                  isActive={isActive}
                  className={tabItem}
                  textClassName={classNames(
                    'text-sm font-medium transition-colors !no-underline',
                    isActive ? '!text-brand-primary' : '!text-muted-foreground hover:!text-brand-primary',
                    item.textClassName,
                  )}
                />
              );
            })}
          </nav>
        </div>
      </div>
      {renderCompanyDetailsModal}
    </>
  );
};

export default PageHeader;

PageHeader.NavigationLink = ({
  to,
  label,
  type,
  className,
  textClassName,
  style,
  isActive,
  onClick,
  onClicked,
  iconRight,
  testId,
}: INavItem): ReactElement => {
  const activeIndicator = isActive ? <span className={tabActiveIndicator} /> : null;

  if (type === 'button') {
    return (
      <button
        data-test={testId}
        onClick={() => {
          onClick?.();
          onClicked?.();
        }}
        className={classNames('text-left shrink-0 relative', className)}
        style={style}
      >
        <p
          className={classNames(
            'text-sm text-muted-foreground hover:underline inline-flex items-center gap-x-2xs',
            textClassName,
          )}
        >
          {label}
          {iconRight}
        </p>
        {activeIndicator}
      </button>
    );
  }

  return (
    <Link
      data-test={testId}
      to={to}
      viewTransition
      className={classNames('shrink-0 relative', className)}
      onClick={() => onClicked?.()}
      style={style}
    >
      <p
        className={classNames(
          'text-sm text-muted-foreground hover:underline inline-flex items-center gap-x-2xs',
          textClassName,
        )}
      >
        {label}
        {iconRight}
      </p>
      {activeIndicator}
    </Link>
  );
};

PageHeader.Back = ({ onClick, title }: IBack): ReactElement => {
  const theme = useTheme();

  return (
    <button
      onClick={onClick}
      className="flex items-center w-full p-md text-sm z-40 bg-brand-background-primary text-muted-foreground lg:p-none lg:w-fit dark:shadow-none"
    >
      <BackIcon
        className="h-3 !w-3 mr-xs"
        color={theme.isDarkMode ? theme.color.brand.icon.color : theme.color.mobile.tertiary}
      />
      {title}
    </button>
  );
};

// Header styles
const headerWrapper = 'bg-brand-background-primary border-b border-brand-border';
const identityRow = 'layout-container flex justify-between items-center py-sm gap-x-sm';
const logoGroup = 'flex items-center gap-x-sm cursor-pointer min-w-0';
const companyName = 'text-sm sm:text-md font-semibold text-brand-text-primary leading-tight truncate';
const companyAddress = 'text-2xs sm:text-xs text-muted-foreground truncate';
const tabRow = 'layout-container overflow-x-auto scrollbar-hide';
const tabNav = 'flex gap-0';
const tabItem = 'relative px-sm sm:px-md py-xs whitespace-nowrap';
const tabActiveIndicator = 'absolute bottom-0 left-0 right-0 h-[2.5px] bg-brand-primary rounded-t-full';
